import { useEffect, useState } from 'react';

import { CATEGORY_TYPE } from '../../../../../constants/service/app/categoryConstants';
import { Reorder } from '../utils';

export const useExhibitCategory = () => {
  // 선택한 전시 카테고리 탭 타입(일반형, 강조형)
  const [exhibitCategoryTabType, setExhibitCategoryTab] = useState(String(CATEGORY_TYPE.GENERAL));

  // 선택한 전시 1차 카테고리 리스트
  const [selectedExhibitMainCategoryList, setSelectedExhibitMainCategoryList] = useState([]);
  // 선택한 전시 2차 카테고리 리스트
  const [selectedExhibitSubCategoryList, setSelectedExhibitSubCategoryList] = useState([]);

  // 전시 카테고리 리스트
  const [exhibitCategoryDataSource, setExhibitCategoryDataSource] = useState([]);
  // 전시 일반 카테고리 리스트
  const [exhibitGeneralCategoryDataSource, setExhibitGeneralCategoryDataSource] = useState([]);
  // 전시 강조형 카테고리 리스트
  const [exhibitEmphasisCategoryDataSource, setExhibitEmphasisCategoryDataSource] = useState([]);

  // 전시 일반, 강조 카테고리 상태 배열에 필터링하여 업데이트
  useEffect(() => {
    if (!exhibitCategoryDataSource?.length) return;

    const orderCategory = (categories) => {
      return categories.map((mainCategory, mainIndex) => ({
        ...mainCategory,
        order: `${mainIndex + 1}`,
        children: mainCategory.children.length
          ? mainCategory.children.map((subCategory, subIndex) => ({
              ...subCategory,
              order: `${mainIndex + 1}-${subIndex + 1}`,
            }))
          : [],
      }));
    };

    setExhibitGeneralCategoryDataSource(
      orderCategory(
        exhibitCategoryDataSource.filter(
          (mainCategory) =>
            // 일반형이 아닌 카테고리일 경우 필터링하여 제외
            mainCategory.type === CATEGORY_TYPE.GENERAL,
        ),
      ),
    );

    setExhibitEmphasisCategoryDataSource(
      orderCategory(
        exhibitCategoryDataSource.filter(
          (mainCategory) =>
            // 일반형이 아닌 카테고리일 경우 필터링하여 제외
            mainCategory.type === CATEGORY_TYPE.EMPHASIS,
        ),
      ),
    );
  }, [exhibitCategoryDataSource]);

  const setExhibitCategoryTabType = (type) => {
    setExhibitCategoryTab(type);
    setSelectedExhibitMainCategoryList([]);
    setSelectedExhibitSubCategoryList([]);
  };

  const onSelectExhibit = (record, selected) => {
    // 전시 카테고리가 선택된 경우
    if (selected) {
      // 선택한 전시 카테고리가 1차 카테고리일 때
      if (!record.parentId) {
        setSelectedExhibitMainCategoryList((prev) => [...prev, record]);

        // 1차 전시 카테고리 선택 시, 해당 하위 2차 카테고리도 모두 자동으로 선택되게 함
        setSelectedExhibitSubCategoryList((prev) => [...prev, ...record.children]);
        return;
      }
      // 선택한 카테고리가 2차 카테고리일 때
      setSelectedExhibitSubCategoryList((prev) => [...prev, record]);
      return;
    }
    // 카테고리 선택이 해제된 경우
    // 선택 해제된 카테고리가 1차 카테고리일 때
    if (!record.parentId) {
      setSelectedExhibitMainCategoryList((prev) => prev.filter((category) => category.id !== record.id));
      // 하위 2차 카테고리도 모두 선택 해제 처리
      setSelectedExhibitSubCategoryList((prev) => prev.filter((category) => category.parentId !== record.id));
      return;
    }
    // 선택 해제된 카테고리가 2차 카테고리일 때
    setSelectedExhibitSubCategoryList((prev) => prev.filter((category) => category.id !== record.id));
  };

  const onSelectAllExhibit = (selected) => {
    const categoryDataSource =
      exhibitCategoryTabType === String(CATEGORY_TYPE.GENERAL)
        ? exhibitGeneralCategoryDataSource
        : exhibitEmphasisCategoryDataSource;

    // 카테고리가 전체 선택된 경우
    if (selected) {
      setSelectedExhibitMainCategoryList(categoryDataSource);
      //2차 카테고리일 때
      setSelectedExhibitSubCategoryList(
        categoryDataSource.reduce((prev, curr) => {
          return [...prev, ...curr.children];
        }, []),
      );

      return;
    }
    // 카테고리가 전체 선택 해제된 경우 선택된 1차, 2차 카테고리 id 배열 상태 빈 배열로 초기화
    setSelectedExhibitMainCategoryList([]);
    setSelectedExhibitSubCategoryList([]);
  };

  const handleDragEnd = (result, dataSource, setDataSource) => {
    if (result.type === 'MAIN') {
      const mainCategories = Reorder(dataSource, result.source.index, result.destination.index);
      setDataSource(
        mainCategories.map((category, index) => ({
          ...category,
          order: String(index + 1),
          children: category.children.map((child, childIndex) => ({
            ...child,
            order: `${index + 1}-${childIndex + 1}`,
          })),
        })),
      );
    } else {
      const targetParent = dataSource.find((main) => main.name === result.type);
      const subCategories = targetParent.children;
      const newList = Reorder(subCategories, result.source.index, result.destination.index);

      const copiedDataSource = JSON.parse(JSON.stringify(dataSource));

      setDataSource(
        copiedDataSource.map((main) => {
          if (main.name === result.type) {
            return {
              ...main,
              children: newList.map((child, childIndex) => ({
                ...child,
                order: `${targetParent.order}-${childIndex + 1}`,
              })),
            };
          } else {
            return main;
          }
        }),
      );
    }
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (exhibitCategoryTabType === String(CATEGORY_TYPE.GENERAL)) {
      handleDragEnd(result, exhibitGeneralCategoryDataSource, setExhibitGeneralCategoryDataSource);
    } else if (exhibitCategoryTabType === String(CATEGORY_TYPE.EMPHASIS)) {
      handleDragEnd(result, exhibitEmphasisCategoryDataSource, setExhibitEmphasisCategoryDataSource);
    }
  };

  return {
    exhibitCategoryTabType,
    setExhibitCategoryDataSource,
    exhibitGeneralCategoryDataSource,
    setExhibitGeneralCategoryDataSource,
    exhibitEmphasisCategoryDataSource,
    selectedExhibitMainCategoryList,
    selectedExhibitSubCategoryList,
    setExhibitEmphasisCategoryDataSource,
    setExhibitCategoryTabType,
    setSelectedExhibitMainCategoryList,
    setSelectedExhibitSubCategoryList,
    onDragEnd,
    onSelectExhibit,
    onSelectAllExhibit,
  };
};
